import { useMemo } from 'react';
import {
  MultipleVsPeersChartWrapper,
  ChartArea,
  EarningsGrowthColumn,
  EarningsGrowthBackground,
  TipContainer,
  TooltipName,
  LabelButton,
  LoadingState,
} from './styled';
import PassFailBackground, { Label } from '../../../PassFailBackground';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';
import { useTheme, Text, LoadingSpinner, Column } from '@simplywallst/ui-core';
import useDimensions from 'react-cool-dimensions';
import BottomAxis from './components/BottomAxis';
import { scaleLinear } from '@visx/scale';
import type { Peer } from '../../MultipleVsPeers';
import type { PreferredMultiple } from '../../../../selector';
import { toSignificant } from '@/utilities/formatting';
import { useTracking } from 'react-tracking';
import findMax from 'lodash/max';
import Tooltip from '@/components/Tooltip';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import PeerLabel from './components/PeerLabel';
import type { QueryStatus } from '@tanstack/react-query';
import { toLocaleUrl } from '@/pages/CompanyReport/utils/toLocaleUrl';
import { useLocale } from '@/hooks/useLocale';
import { useValuationTranslation } from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';

interface Props {
  companyId: string;
  peers: Peer[];
  peersAverage: number | null;
  preferredMultiple: PreferredMultiple;
  insufficientData?: boolean;
  status?: QueryStatus;
}

const MultipleVsPeersChart = ({
  peers,
  preferredMultiple,
  companyId,
  peersAverage,
  insufficientData,
  status,
}: Props) => {
  const theme = useTheme();
  const getTerm = useGlossaryTerms();
  const { noirGradient02 } = useChartDefinitionsContext();
  const { observe, width: chartWidth } = useDimensions();
  const { trackEvent } = useTracking();
  const locale = useLocale();

  const numTicks = 4;
  const height = 330;
  const topMargin = 36;
  const gutterTop = 8;
  const gutterBottom = 8;
  const rowMargin = 4;
  const rowHeight =
    (height - topMargin - gutterTop - gutterBottom) / (peers.length || 1);

  const { sortedPeers, max, xPercentScale, tickValues } = useMemo(() => {
    const sortedPeers = peers.sort(
      (a, b) =>
        Number(b.preferredCurrentRatio) - Number(a.preferredCurrentRatio)
    );
    const maxRatio = findMax([...peers.map((p) => p.ratio), peersAverage]) || 0;
    //In increments of 0.1 if max lower than 10
    const increment = maxRatio >= 10 ? 1 : 10;
    const rounding = Math.ceil((maxRatio * increment) / numTicks) / increment;
    const max = rounding * numTicks;
    const xPercentScale = scaleLinear<number>({
      range: [0, 100],
      domain: [0, max],
    });

    const tickValues = max
      ? Array.from({ length: numTicks + 1 }).map((_, i) =>
          toSignificant(i * rounding, 1)
        )
      : [];

    return {
      sortedPeers,
      max,
      xPercentScale,
      tickValues,
    };
  }, [peers, peersAverage]);

  const average = peersAverage || 0;
  const averageXPercent = (average / max) * 100 || 100;

  const xScale = scaleLinear<number>({
    range: [0, chartWidth],
    domain: [0, max],
  });

  const reverseAvgLabel =
    chartWidth > 0 && (averageXPercent * chartWidth) / 100 < 114;

  const t = useValuationTranslation();

  const growthTerm =
    preferredMultiple === 'PS'
      ? t('vsPeers.chart.header.psGrowth')
      : t('vsPeers.chart.header.peGrowth');

  const growthTermIndex =
    preferredMultiple === 'PS'
      ? 'estimated sales growth'
      : 'estimated earnings growth';
  const targetLinkProp = RUNTIME_ENV === 'browser' && {
    target: '_blank',
  };
  return (
    <MultipleVsPeersChartWrapper data-cy-id="ratio-vs-peers-chart">
      <ChartArea ref={observe}>
        <svg width="100%" height="100%">
          <>
            <PassFailBackground
              y={topMargin}
              height={height - topMargin}
              percent={averageXPercent}
            />
            {/* PEER AVG LABEL */}
            {/* This check prevents the label from flashing when switching to data table */}
            {chartWidth > 0 && (
              <Label
                x={xScale(average)}
                y={topMargin}
                multiple={peersAverage}
                fill={theme.color.neutral}
                fontColor={theme.color.noir}
                height={height - topMargin}
                chartWidth={chartWidth}
                lineWidth={2}
                title={t('vsPeers.chart.header.peerAverage')}
                anchor={reverseAvgLabel ? 'start' : 'end'}
                letterWidth={7}
                showMultiple={!insufficientData}
              />
            )}
          </>

          <BottomAxis
            top={height}
            xPercentScale={xPercentScale}
            preferredMultiple={preferredMultiple}
            tickValues={tickValues}
          />
          {/* PEERS BAR ROWS */}
          {sortedPeers.map((peer, i) => {
            const selected =
              peer.companyId.toLowerCase() === companyId.toLowerCase();
            const barWidthPercent = xPercentScale(peer.ratio || 0);
            const barWidth = xScale(peer.ratio || 0);
            const yCoord = topMargin + gutterTop + 3 + i * rowHeight;
            const barHeight = rowHeight - rowMargin;
            const peerValuationUrl = `${toLocaleUrl(
              locale,
              peer.canonicalURL
            )}/valuation`;
            return (
              <g key={peer.companyId}>
                <rect
                  fill={selected ? theme.color.noir : noirGradient02}
                  y={yCoord}
                  width={barWidthPercent + '%'}
                  height={barHeight}
                />
                <rect
                  fill={selected ? theme.color.brand01 : theme.color.white}
                  x={barWidthPercent + '%'}
                  y={yCoord}
                  width={2}
                  height={barHeight}
                  transform={'translate(-2,0)'}
                />
                <foreignObject y={yCoord} width={chartWidth} height={barHeight}>
                  <LabelButton
                    display="block"
                    as="a"
                    href={selected ? undefined : peerValuationUrl}
                    title={peer.name}
                    {...targetLinkProp}
                    onClick={() => {
                      if (selected) return;
                      trackEvent({
                        action: 'click',
                        modifier: 'vsPeersChart',
                        companyId: peer.companyId,
                      });
                    }}
                    rel="noreferrer"
                  >
                    <PeerLabel
                      peerName={peer.name}
                      peerRatio={peer.ratioFormatted}
                      selected={selected}
                      chartWidth={chartWidth}
                      barWidth={barWidth}
                      barWidthPercent={barWidthPercent}
                    />
                  </LabelButton>
                </foreignObject>
              </g>
            );
          })}
        </svg>
      </ChartArea>
      <EarningsGrowthColumn height={height + 16}>
        <foreignObject x={0} y={4} width={50} height={height + 16}>
          <Tooltip
            openDelay={300}
            light
            title={<TipContainer>{getTerm(growthTermIndex)}</TipContainer>}
          >
            <TooltipName>{growthTerm}</TooltipName>
          </Tooltip>
        </foreignObject>

        <EarningsGrowthBackground
          rx="4"
          width="100%"
          height={height - topMargin}
          y={topMargin}
        />
        {sortedPeers.map((peer, i) => {
          const selected = peer.companyId === companyId;
          const peerValuationUrl = `${toLocaleUrl(
            locale,
            peer.canonicalURL
          )}/valuation`;
          return (
            <foreignObject
              key={peer.companyId}
              width={'100%'}
              height={20}
              y={topMargin + gutterTop + rowMargin + (i + 0.5) * rowHeight - 15}
            >
              <LabelButton
                display="block"
                as="a"
                marginLeft
                href={selected ? undefined : peerValuationUrl}
                {...targetLinkProp}
                onClick={() => {
                  if (selected) return;
                  trackEvent({
                    action: 'click',
                    modifier: 'vsPeersChart',
                    companyId: peer.companyId,
                  });
                }}
              >
                <Text typography="xSmall">
                  {preferredMultiple === 'PS'
                    ? peer.salesGrowthFormatted
                    : peer.earningsGrowthFormatted}
                </Text>
              </LabelButton>
            </foreignObject>
          );
        })}
      </EarningsGrowthColumn>
      {status === 'pending' && (
        <LoadingState>
          <Column
            col={12}
            displayFlex
            justifyContent="center"
            alignItem="center"
            column
          >
            <LoadingSpinner light />
            <br />
            <Text typography="tiny">{t('vsPeers.chart.header.fetching')}</Text>
          </Column>
        </LoadingState>
      )}
    </MultipleVsPeersChartWrapper>
  );
};
export default MultipleVsPeersChart;
